import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LayoutGrid, LayoutDashboard, ShieldCheck, Bell, LogOut, Landmark, Building2, Star, Menu, X, HelpCircle, Palette, Check,
} from "lucide-react";

const THEMES = [
  { id: "default", label: "Défaut (Or & Bleu)" },
  { id: "dark", label: "Sombre" },
  { id: "light", label: "Claire" },
];

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-2 rounded-full hover:bg-slate-100 transition-colors" data-testid="theme-toggle" title="Changer de thème">
          <Palette className="h-5 w-5 text-slate-600" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Thème d'affichage</div>
        {THEMES.map((t) => (
          <DropdownMenuItem key={t.id} onClick={() => setTheme(t.id)} data-testid={`theme-${t.id}`} className="justify-between">
            {t.label}
            {theme === t.id && <Check className="h-4 w-4 text-congo-blue" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const Logo = () => (
  <Link to="/" className="flex items-center" data-testid="logo-home">
    <div className="rounded-xl bg-congo-blue px-3 py-1 flex items-center shadow-sm">
      <img src="/leoparts-full.png" alt="LéoParts Capital Markets" className="h-20 w-auto object-contain" />
    </div>
  </Link>
);

function NotifBell() {
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const unread = notifs.filter((n) => !n.read).length;
  const load = useCallback(async () => {
    try { const { data } = await api.get("/notifications/me"); setNotifs(data); } catch (e) { console.error("Failed to load notifications", e); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const markRead = async () => { await api.post("/notifications/read"); load(); };
  return (
    <DropdownMenu onOpenChange={(o) => { setOpen(o); if (o) markRead(); }}>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-slate-100 transition-colors" data-testid="notif-bell">
          <Bell className="h-5 w-5 text-slate-600" />
          {unread > 0 && <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-congo-red text-white text-[10px] flex items-center justify-center font-bold">{unread}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-auto">
        <div className="px-3 py-2 font-semibold text-sm">Notifications</div>
        <DropdownMenuSeparator />
        {notifs.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">Aucune notification</div>}
        {notifs.map((n) => (
          <div key={n.id} className="px-3 py-2 border-b last:border-0" data-testid="notif-item">
            <p className="text-sm font-medium text-slate-800">{n.title}</p>
            <p className="text-xs text-muted-foreground">{n.message}</p>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = user && ["cadeco", "studeur", "admin"].includes(user.role);
  const doLogout = async () => { await logout(); navigate("/"); };

  const navLinks = [
    { to: "/projets", label: "Projets", icon: LayoutGrid },
    { to: "/faq", label: "FAQ", icon: HelpCircle },
    ...(user ? [{ to: "/tableau-de-bord", label: "Tableau de bord", icon: LayoutDashboard }] : []),
    ...(user && (user.role === "cadeco" || user.role === "admin") ? [{ to: "/cadeco", label: "CADECO", icon: Landmark }] : []),
    ...(user && (user.role === "studeur" || user.role === "admin") ? [{ to: "/studeur", label: "Studeur", icon: Building2 }] : []),
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 h-24 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo />
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${loc.pathname.startsWith(l.to) ? "bg-congo-bluelight text-congo-blue" : "text-slate-600 hover:bg-slate-100"}`}>
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {user ? (
              <>
                <NotifBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 p-1 rounded-full hover:bg-slate-100" data-testid="user-menu">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={user.picture} />
                        <AvatarFallback className="bg-congo-blue text-white text-sm">{(user.name || user.email)[0].toUpperCase()}</AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-2">
                      <p className="text-sm font-semibold truncate">{user.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      <Badge className="mt-1 bg-congo-bluelight text-congo-blue border-0 capitalize">{user.role}</Badge>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/tableau-de-bord")} data-testid="menu-dashboard"><LayoutDashboard className="h-4 w-4 mr-2" />Tableau de bord</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/kyc")} data-testid="menu-kyc"><ShieldCheck className="h-4 w-4 mr-2" />Vérification KYC</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={doLogout} className="text-congo-red" data-testid="menu-logout"><LogOut className="h-4 w-4 mr-2" />Déconnexion</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Button variant="ghost" onClick={() => navigate("/auth")} data-testid="header-login-btn" className="rounded-full">Connexion</Button>
                <Button onClick={() => navigate("/auth?mode=register")} data-testid="header-register-btn" className="rounded-full bg-congo-blue hover:bg-congo-bluedark">Investir</Button>
              </div>
            )}
            <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg text-slate-700 hover:bg-slate-100">
                <l.icon className="h-5 w-5" />{l.label}
              </Link>
            ))}
            {!user && (
              <div className="pt-2 flex gap-2">
                <Button variant="outline" className="flex-1 rounded-full" onClick={() => { setMobileOpen(false); navigate("/auth"); }}>Connexion</Button>
                <Button className="flex-1 rounded-full bg-congo-blue" onClick={() => { setMobileOpen(false); navigate("/auth?mode=register"); }}>Investir</Button>
              </div>
            )}
          </div>
        )}
      </header>

      <main className="flex-1 pb-20 md:pb-0">{children}</main>

      {/* Mobile bottom nav */}
      {user && (
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 h-16 flex items-center justify-around shadow-md" data-testid="bottom-nav">
          {navLinks.slice(0, 4).map((l) => {
            const active = loc.pathname.startsWith(l.to);
            return (
              <Link key={l.to} to={l.to} className="flex flex-col items-center gap-1 px-3 relative">
                <l.icon className={`h-5 w-5 ${active ? "text-congo-blue" : "text-slate-400"}`} />
                <span className={`text-[10px] ${active ? "text-congo-blue font-semibold" : "text-slate-400"}`}>{l.label.split(" ")[0]}</span>
                {active && <span className="absolute -bottom-0 h-0.5 w-8 bg-congo-gold rounded-full" />}
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
}
